// Idioma de la interfaz → formatos de fecha, número y moneda (WS-2274).
//
// Único punto que traduce el idioma de ngx-translate («es», «ca», «en») a un
// locale. Úsalo en vez de escribir 'es-ES' a mano:
//   - TS:        `formatNumber(n, this.translate.currentLang)`,
//                `new Intl.DateTimeFormat(dateLocaleFor(this.translate.currentLang), …)`
//   - plantilla: `| date`, `| number`, `| currency` ya siguen el idioma (LOCALE_ID
//                se fija al arrancar, app.module.ts; cambiar de idioma recarga), o
//                `{{ fecha | langDate: 'dd MMM yyyy' }}` (shared/pipes/lang-date.pipe.ts).
// Los mensajes que se envían al usuario final (p. ej. payment-diagnosis) van
// en el idioma de ese usuario, no en el de la interfaz: no usan esto.

/** Idiomas de la interfaz. */
export type UiLang = 'es' | 'ca' | 'en';

/** Normaliza cualquier código («ca-ES», «EN», null…) a un idioma de la interfaz; por defecto «es». */
export function uiLang(lang: string | null | undefined): UiLang {
  const short = (lang ?? '').slice(0, 2).toLowerCase();
  return short === 'ca' || short === 'en' ? short : 'es';
}

/** Idioma de la interfaz → locale de `Intl` («es-ES», «ca-ES», «en-GB»). */
export function dateLocaleFor(lang: string | null | undefined): string {
  switch (uiLang(lang)) {
    case 'ca':
      return 'ca-ES';
    case 'en':
      return 'en-GB';
    default:
      return 'es-ES';
  }
}

/**
 * Idioma de la interfaz → locale registrado en Angular («es», «ca», «en-GB»;
 * ver `registerLocaleData` en app.module.ts) para `formatDate`/`DatePipe`.
 */
export function angularLocaleFor(lang: string | null | undefined): string {
  switch (uiLang(lang)) {
    case 'ca':
      return 'ca';
    case 'en':
      return 'en-GB';
    default:
      return 'es';
  }
}

/** Número con separadores del idioma: 1.234,5 / 1,234.5. */
export function formatNumber(
  value: number | null | undefined,
  lang: string | null | undefined,
  options: Intl.NumberFormatOptions = {},
): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString(dateLocaleFor(lang), options);
}

/** Céntimos → «12,50 €» / «€12.50» según el idioma. */
export function formatEuroCentsFor(cents: number | null | undefined, lang: string | null | undefined): string {
  if (cents === null || cents === undefined || Number.isNaN(Number(cents))) return '—';
  return (Number(cents) / 100).toLocaleString(dateLocaleFor(lang), { style: 'currency', currency: 'EUR' });
}

/**
 * Idioma activo de la interfaz para utilidades puras (sin Angular) que no
 * reciben el idioma por parámetro, p. ej. `formatEuroCents` de
 * passenger-charge.ts. Lo mantiene al día TranslationService (onLangChange).
 */
let activeLang: UiLang = 'es';

export function setActiveLang(lang: string | null | undefined): void {
  activeLang = uiLang(lang);
}

/** Locale de `Intl` del idioma activo («es-ES», «ca-ES», «en-GB»). */
export function activeIntlLocale(): string {
  return dateLocaleFor(activeLang);
}
